import { RootState } from '@/models/root-state.model';
import { createSelector } from 'reselect';
import { selectUser, selectUserIsGameTester } from '../selectors/userSelectors';
import { selectIpData, selectLocale } from '../selectors/commonSelectors';
import { searchGames } from '@/shared/gameHelpers';
import { GameCardVM } from '@/models/vm/game-card-vm.model';
import { isRegulatedLocale } from '@/shared/utility';
import { shouldExcludeGame, getFilteredProviderShortDescriptions } from '@/utils/games-utils';
import {
  BffFavouriteGamesResponse,
  BffGamesCategory,
  BffGamesJackpotsResponse,
  BffGamesLastPlayedResponse,
  BffGamesResponse,
} from '@lucky7ventures/bff-types';
import {
  envIsCanada,
  envIsNormal,
  REWARD_WHEEL_GAMES,
  REWARD_WHEEL_GAMES_REG,
} from '@/constants/constants';

export const selectGames = (state: RootState): BffGamesResponse['games'] => state.bffGames.games;
export const selectGamesProviders = (state: RootState): BffGamesResponse['providers'] =>
  state.bffGames.providers;
export const selectGamesCategories = (state: RootState): BffGamesResponse['categories'] =>
  state.bffGames.categories;
export const selectGamesLoading = (state: RootState): boolean => state.bffGames.loading;
export const selectGamesLastPlayedIds = (state: RootState): BffGamesLastPlayedResponse =>
  state.bffGames.lastPlayedIds;
export const selectFavouriteGamesIds = (state: RootState): BffFavouriteGamesResponse =>
  state.bffGames.favouriteGamesIds;
export const selectGamesJackpots = (state: RootState): BffGamesJackpotsResponse =>
  state.bffGames.jackpots;
export const selectGameById =
  (id: number) =>
  (state: RootState): GameCardVM => {
    return {
      ...state.bffGames.games[id],
      jackpot: state.bffGames.jackpots[id],
    };
  };

const mapGamesToVM = (games: Record<number, GameCardVM>) => (acc: GameCardVM[], id: number) => {
  const game = games[id];
  if (!game) {
    return acc;
  }
  acc.push(game);
  return acc;
};

const selectFilteredGames = createSelector(
  selectGames,
  selectGamesJackpots,
  selectUser,
  selectLocale,
  selectUserIsGameTester,
  selectIpData,
  (games, jackpots, user, locale, gameTester, ipData): Record<number, GameCardVM> => {
    return Object.keys(games).reduce((acc: Record<number, GameCardVM>, key: string) => {
      const gameId = parseInt(key);
      const game = games[gameId];
      const jackpot = jackpots[gameId];
      if (!game || shouldExcludeGame(game, user, locale, gameTester, ipData)) {
        return acc;
      }
      acc[gameId] = { ...game, jackpot };
      return acc;
    }, {});
  },
);

export const selectLastPlayedGames = createSelector(
  selectFilteredGames,
  selectGamesLastPlayedIds,
  (games, lastPlayedIds): GameCardVM[] => lastPlayedIds.reduce(mapGamesToVM(games), []),
);

export const selectFavouriteGames = createSelector(
  selectFilteredGames,
  selectFavouriteGamesIds,
  (games, favouriteGamesIds): GameCardVM[] => favouriteGamesIds.reduce(mapGamesToVM(games), []),
);

export const selectGamesByIds = (gameIds: number[]) =>
  createSelector(selectFilteredGames, (games): GameCardVM[] =>
    gameIds.reduce(mapGamesToVM(games), []),
  );

export const selectGamesByCategory = (category: BffGamesCategory | undefined, limit = 0) =>
  createSelector(
    selectFilteredGames,
    selectGamesCategories,
    selectFavouriteGames,
    selectLastPlayedGames,
    (games, categories, favouriteGames, lastPlayedGames): GameCardVM[] => {
      if (!category) {
        return [];
      }
      if (category === BffGamesCategory.FavouriteGames) {
        return favouriteGames;
      }
      if (category === BffGamesCategory.LastPlayed) {
        return lastPlayedGames;
      }
      const selectedCategory = categories[category];
      if (!selectedCategory) {
        return [];
      }
      const gameIds =
        limit === 0
          ? selectedCategory
          : selectedCategory.slice(0, Math.min(limit, selectedCategory.length));
      return gameIds.reduce(mapGamesToVM(games), []);
    },
  );

export const selectGamesByProvider = (provider: string) =>
  createSelector(
    selectFilteredGames,
    selectGamesProviders,
    (games, providers): { name: string; games: GameCardVM[] } | null => {
      const selectedProvider = providers[provider];
      if (!selectedProvider) {
        return null;
      }
      return {
        name: selectedProvider.name,
        games: selectedProvider.games.reduce(mapGamesToVM(games), []),
      };
    },
  );

export const selectGamesByProviderAndCategory = (
  provider: string | undefined,
  category: BffGamesCategory | undefined,
) =>
  createSelector(
    selectFilteredGames,
    selectGamesProviders,
    selectGamesCategories,
    (games, providers, categories): { name: string; games: GameCardVM[] } | null => {
      if (!provider || !category) {
        return null;
      }
      // check if the Provider exists
      const selectedProvider = providers[provider];
      if (!selectedProvider) {
        return null;
      }
      // if the provider exists, find if the category exists
      const selectedCategory = categories[category];
      if (!selectedCategory) {
        return null;
      }
      // if both exists, create an interesection of Provider Games and Category Games, to get games which fall into both
      const categoryGamesSet = new Set(selectedCategory);
      const gamesIntersection = selectedProvider.games.filter(gameId =>
        categoryGamesSet.has(gameId),
      );
      return {
        name: selectedProvider.name,
        games: gamesIntersection.reduce(mapGamesToVM(games), []),
      };
    },
  );

export const selectAllGames = selectGamesByCategory(BffGamesCategory.AllGames);
export const selectNewGames = createSelector(
  selectGamesByCategory(BffGamesCategory.NewGames),
  selectLocale,
  (games, locale) => {
    // As a Business rule we want to limit New Games
    // 36 - for Regulated Locales
    // 54 - for non-Regulated Locales
    const limit = isRegulatedLocale(locale) ? 36 : 54;
    return games.slice(0, Math.min(limit, games.length));
  },
);
export const selectJackpotsGames = selectGamesByCategory(BffGamesCategory.Jackpots);
export const selectTableGames = selectGamesByCategory(BffGamesCategory.TableGames);
export const selectSlotsGames = selectGamesByCategory(BffGamesCategory.Slots);
export const selectWinterGames = selectGamesByCategory(BffGamesCategory.WinterGames);
export const selectHalloweenGames = selectGamesByCategory(BffGamesCategory.HalloweenGames);
export const selectMegawaysGames = selectGamesByCategory(BffGamesCategory.Megaways);
export const selectBuyFeatureGames = selectGamesByCategory(BffGamesCategory.BuyFeature);
export const selectIGTGames = selectGamesByProvider(BffGamesCategory.IGTGames);
export const selectOntarioPlayersLoveGames = selectGamesByCategory(
  BffGamesCategory.OntarioPlayersLove,
);
export const selectTheNewReelThingGames = selectGamesByCategory(BffGamesCategory.TheNewReelThing);
export const selectGameShowsGames = selectGamesByCategory(BffGamesCategory.GameShows);
export const selectBlackjackGames = selectGamesByCategory(BffGamesCategory.Blackjack);
export const selectBaccaratGames = selectGamesByCategory(BffGamesCategory.Baccarat);
export const selectRouletteGames = selectGamesByCategory(BffGamesCategory.Roulette);
export const selectPokerGames = selectGamesByCategory(BffGamesCategory.Poker);
export const selectAsianGamesGames = selectGamesByCategory(BffGamesCategory.AsianGames);
export const selectCrashGamesGames = selectGamesByCategory(BffGamesCategory.CrashGames);
export const selectArcadeGamesGames = selectGamesByCategory(BffGamesCategory.ArcadeGames);
export const selectPlinkoGames = selectGamesByCategory(BffGamesCategory.Plinko);
export const selectPopularGames = selectGamesByCategory(BffGamesCategory.Popular);
export const selectVirtualSportsGames = selectGamesByCategory(BffGamesCategory.VirtualSports);

const selectAllLiveCasinoGames = createSelector(
  selectGameShowsGames,
  selectBlackjackGames,
  selectBaccaratGames,
  selectRouletteGames,
  selectPokerGames,
  selectAsianGamesGames,
  (gameShowsGames, blackjackGames, baccaratGames, rouletteGames, pokerGames, asianGamesGames) =>
    gameShowsGames
      .concat(blackjackGames)
      .concat(baccaratGames)
      .concat(rouletteGames)
      .concat(pokerGames)
      .concat(asianGamesGames),
);
export const selectLiveCasinoGames = envIsCanada()
  ? selectAllLiveCasinoGames
  : selectGamesByCategory(BffGamesCategory.LiveCasino);

export const selectRecommendedGames = createSelector(
  selectGamesByCategory(BffGamesCategory.Recommended),
  games => {
    // As a Business Rule we want to limit Recommended games to 18
    return games.slice(0, Math.min(18, games.length));
  },
);

export const selectGamesHomeRegulated = createSelector(selectAllGames, games =>
  games.slice(0, Math.min(12, games.length)),
);

const selectGamesBySearch = (_: RootState, search: string) => search;
export const selectSearchedGames = createSelector(
  selectAllGames,
  selectGamesBySearch,
  (games, search) => searchGames(search, games),
);

export const selectRewardsReelFeaturedGames = createSelector(
  selectAllGames,
  (games: GameCardVM[]): GameCardVM[] => {
    if (envIsNormal()) {
      return games.filter(i => REWARD_WHEEL_GAMES.includes(i.slug));
    }

    return games.filter(i => REWARD_WHEEL_GAMES_REG.includes(i.slug));
  },
);

export const selectProvidersByCategory = (category: BffGamesCategory) =>
  createSelector(selectGamesCategories, selectGamesProviders, (categories, providers) => {
    const categoryGames = categories[category];
    const providerSlugs = Object.keys(providers);
    if (!categoryGames || categoryGames.length === 0 || providerSlugs.length === 0) {
      return [];
    }
    const categoryGamesSet = new Set(categoryGames);

    return providerSlugs.filter(providerSlug => {
      return providers[providerSlug].games.some(gameId => categoryGamesSet.has(gameId));
    });
  });

export const selectGameProviders = (providerSlugs: string[]) =>
  createSelector(selectGamesProviders, providers => {
    return providerSlugs.filter(providerSlug => {
      const provider = providers[providerSlug];
      return !!provider;
    });
  });

export const selectGameProvidersList = createSelector(
  selectGamesProviders,
  selectUser,
  selectLocale,
  selectUserIsGameTester,
  selectIpData,
  (providers, user, locale, gameTester, ipData) => {
    const filteredProviderNames = getFilteredProviderShortDescriptions(
      user,
      locale,
      gameTester,
      ipData,
    );

    return Object.keys(providers)
      .filter(key => {
        const provider = providers[key];
        // Filter out providers whose name is in the filtered list
        // Trim provider name to handle leading/trailing spaces
        const trimmedName = provider.name.trim();
        return !filteredProviderNames.includes(trimmedName);
      })
      .sort()
      .filter(i => i)
      .map(key => ({ slug: key, name: providers[key].name }));
  },
);

export const selectIsGameFavourited = (gameId: number) =>
  createSelector(selectFavouriteGames, favouriteGames =>
    favouriteGames.some(favouriteGame => favouriteGame.gameId === gameId),
  );
